import React, {useState, useEffect} from 'react';
import axios from 'axios';
import AdminMenu from "../AdminMenu";
import AdminHeader from "../AdminHeader";
import Button from "../../../components/elements/Button";
import InputField from "../../../components/elements/InputField";
import ImageUpload from "../../../components/elements/ImageUpload";
import TagsInput from "../../../components/elements/TagsInput";
import ErrorMessage from "../../../components/layout/ErrorMessage";
import {useNavigate, useParams} from 'react-router-dom';
import {ADMIN_API_BASE} from "../../../utils/apiConfig";
import CustomSwitch from "../../../components/elements/CustomSwitch";
import CustomSelect from "../../../components/elements/CustomSelect";
import { PUBLIC_S3_BASE } from "../../../utils/apiConfig";


const CourseOverview = () => {
    const {id: urlId} = useParams();
    const navigate = useNavigate();

    const isNewCourse = urlId == null || urlId === "new";
    const realCourseId = isNewCourse ? "new" : urlId;

    const [course, setCourse] = useState({
        id: realCourseId,
        price: 0,
        category: "",
        difficulty: 0,
        paid_levels: 0,
        prerequisites: [],
        image: '',
        icon: '',
        is_active: false
    });

    const [message, setMessage] = useState({type: '', text: ''});

    const getCsrfToken = async () => {
        const res = await fetch(`${ADMIN_API_BASE}/csrf-token`, {
            credentials: 'include'
        });
        const data = await res.json();
        return data.csrfToken;
    };

    const [categoryOptions, setCategoryOptions] = useState([]);
    const [coursesReferences, setCoursesReferences] = useState([]);

    const fetchCategories = async () => {
        try {
            const token = await getCsrfToken();
            const res = await axios.get(`${ADMIN_API_BASE}/categories`, {
                headers: {'csrf-token': token},
                withCredentials: true
            });
            if (res.data.categories.success) {
                const result = res.data.categories.categories;
                let categoriesArr = [];
                for (const category of result) {
                    categoriesArr.push(category.id);
                }
                setCategoryOptions(categoriesArr);
            }
        } catch (err) {
            setMessage(err);
        }
    }

    useEffect(() => {
        fetchCategories();
        fetchCourses();
    }, [realCourseId]);

    const fetchCourses = async () => {
        try {
            const token = await getCsrfToken();
            const res = await axios.get(`${ADMIN_API_BASE}/course`, {
                headers: {'csrf-token': token},
                withCredentials: true
            });
            setCoursesReferences(res.data.data.map(i => i.id));
        } catch (err) {
            setMessage(err);
        }
    }

    useEffect(() => {
        if (realCourseId !== "new") {
            const load = async () => {
                try {
                    const token = await getCsrfToken();
                    const res = await axios.get(`${ADMIN_API_BASE}/course/${realCourseId}`, {
                        headers: {'csrf-token': token},
                        withCredentials: true
                    });
                    if (res.data.draft) {
                        const draft = res.data.draft;

                        const CDN = PUBLIC_S3_BASE;

                        setCourse({
                            ...draft,

                            image: draft.image
                                ? `${CDN}/${draft.image}`
                                : "",

                            icon: draft.icon
                                ? `${CDN}/${draft.icon}`
                                : ""
                        });
                    }
                } catch (err) {
                    setMessage(err);
                }
            };
            load();
        } else {
            setCourse({
                id: "new",
                price: 0,
                category: "",
                difficulty: 0,
                paid_levels: 0,
                prerequisites: [],
                image: '',
                icon: '',
                is_active: false
            });
        }
    }, [realCourseId]);

    const handleChange = (name, value) => {
        setCourse(prev => ({...prev, [name]: value}));
    };

    const handleChangeId = (id) => {
        setCourse(prev => ({...prev, id: id}));
    };

    const handleImageChange = (file) => {
        setCourse(prev => ({...prev, image: file}));
    };

    const handleIconChange = (file) => {
        setCourse(prev => ({...prev, icon: file}));
    };

    const saveCourse = async () => {
        const formData = new FormData();

        // Ensure `id` is sent for new course
        if (course.id) {
            formData.append('id', course.id);
        }

        if (course.prerequisites && course.prerequisites.length > 0) {
            formData.append('prerequisites', course.prerequisites);
        }

        Object.entries(course).forEach(([key, value]) => {
            if (key === "image" || key === "icon" || key === "id" || key === "prerequisites") return;
            if (value === undefined || value === null || value === "") return;
            formData.append(key, value);
        });

        if (course.image instanceof File) {
            formData.append("image", course.image);
        }

        if (course.icon instanceof File) {
            formData.append("icon", course.icon);
        }

        try {
            const token = await getCsrfToken();
            let res;
            if (!isNewCourse) {
                res = await axios.patch(`${ADMIN_API_BASE}/course/${course.id}`, formData, {
                    headers: {'csrf-token': token},
                    withCredentials: true
                });
            } else {
                res = await axios.post(`${ADMIN_API_BASE}/course/`, formData, {
                    headers: {'csrf-token': token},
                    withCredentials: true
                });

                if (res.data.id) {
                    navigate(`/admin/course/${res.data.id}/overview/`);
                }
            }

            setCourse(prev => ({
                ...prev,
                image: res.data.image ?? prev.image,
                icon: res.data.icon ?? prev.icon
            }));

            setMessage('دوره با موفقیت ذخیره شد!');
        } catch (err) {
            console.log(err);
            setMessage(err);
        }
    };

    return (
        <div className="flex flex-1">
            <div className="w-1/5">
                <AdminMenu path="./"/>
            </div>
            <div className="flex-grow w-4/5">
                <AdminHeader title={isNewCourse ? 'دوره‌ی جدید' : 'ویرایش دوره'}/>

                {message.text && <ErrorMessage type={message.type} message={message.text}/>}

                <form onSubmit={e => e.preventDefault()} className="my-6 gap-8 flex flex-col max-w-2xl">

                    {
                        isNewCourse ? (<InputField title="ID" value={course.id}
                                                   onChange={e => handleChangeId(e.target.value)}/>) : (
                            <InputField title="ID" value={realCourseId} disabled={true}/>)
                    }

                    <CustomSelect title={`دسته‌بندی`} options={categoryOptions} value={course.category}
                                  onChange={e => handleChange('category', e.target.value)}/>

                    <InputField title="قیمت (تومان)" type="number" value={course.price}
                                onChange={e => handleChange('price', +e.target.value)}/>

                    <InputField title="paid levels" type="number" value={course.paid_levels}
                                onChange={e => handleChange('paid_levels', e.target.value)}/>

                    <InputField title="سختی" value={course.difficulty} type="number"
                                onChange={e => handleChange('difficulty', e.target.value)}/>

                    <TagsInput
                        title="پیش‌نیاز‌ها"
                        name="prerequisites"
                        value={course.prerequisites}
                        options={coursesReferences}
                        onChange={(tags) => handleChange("prerequisites", tags)}
                    />
                    <div>
                        <label className="block font-bold text-gray-700 mb-2">تصویر دوره</label>
                        <ImageUpload
                            existingImage={typeof course.image === 'string' ? course.image : null}
                            onFileChange={handleImageChange}
                            name="image"
                        />
                    </div>

                    <div>
                        <label className="block font-bold text-gray-700 mb-2">آیکون دوره</label>
                        <ImageUpload
                            existingImage={typeof course.icon === 'string' ? course.icon : null}
                            onFileChange={handleIconChange}
                            name="icon"
                        />
                    </div>

                    <div className="flex items-center gap-3 my-5">
                        <CustomSwitch checked={course.is_active} label={'دوره فعال باشد'}
                                      onChange={(value) => handleChange('is_active', value)}
                        />
                    </div>
                    <Button onClick={saveCourse}>
                        <span>{isNewCourse ? 'ایجاد دوره' : 'بروزرسانی دوره'}</span>
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default CourseOverview;
